/**
 * Theme system for embed widget.
 * Generates CSS custom properties from theme config.
 */

import type { ThemeConfig } from '../types'

const FONT_FAMILY_MAP: Record<string, string> = {
  system:
    "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  sans: "'Inter', system-ui, -apple-system, sans-serif",
  serif: "'Iowan Old Style', Georgia, 'Times New Roman', serif",
  mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
}

const SHADOW_MAP: Record<string, string> = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.08)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.08)',
}

/** Field gap and outer padding, in that order. */
const SPACING_MAP: Record<string, { gap: string; padding: string }> = {
  compact: { gap: '0.75rem', padding: '0.75rem' },
  normal: { gap: '1rem', padding: '1rem' },
  relaxed: { gap: '1.5rem', padding: '1.5rem' },
}

const BORDER_RADIUS_MAP: Record<string, string> = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
}

// Default colors matching the main app design system
const DEFAULT_COLORS = {
  light: {
    primary: '#e86c4a', // Coral/terracotta
    background: '#faf8f5', // Warm cream
    text: '#1a1a1a',
    muted: '#6b6b6b',
    border: '#e5e0db',
    inputBg: '#ffffff',
  },
  dark: {
    primary: '#f07d5e',
    background: '#1a1a1a',
    text: '#f5f5f5',
    muted: '#a0a0a0',
    border: '#333333',
    inputBg: '#262626',
  },
}

/**
 * Check if user prefers dark mode.
 */
function prefersDarkMode(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Resolve the actual color mode based on config.
 */
export function resolveColorMode(mode: ThemeConfig['mode']): 'light' | 'dark' {
  if (mode === 'auto') {
    return prefersDarkMode() ? 'dark' : 'light'
  }
  return mode || 'light'
}

/**
 * Generate CSS variables string from theme config.
 */
export function resolveFontFamily(fontFamily: ThemeConfig['fontFamily']): string {
  if (!fontFamily) return FONT_FAMILY_MAP.sans
  // A preset name maps to a stack; anything else is taken as a CSS font stack
  // the embedding site chose itself.
  return FONT_FAMILY_MAP[fontFamily] || fontFamily
}

export function generateCSSVariables(theme: ThemeConfig = {}): string {
  const mode = resolveColorMode(theme.mode)
  const defaults = DEFAULT_COLORS[mode]
  const spacing = SPACING_MAP[theme.spacing || 'normal']

  return `
    --syv-primary: ${theme.primaryColor || defaults.primary};
    --syv-background: ${theme.backgroundColor || defaults.background};
    --syv-text: ${theme.textColor || defaults.text};
    --syv-muted: ${defaults.muted};
    --syv-border: ${defaults.border};
    --syv-input-bg: ${defaults.inputBg};
    --syv-font: ${resolveFontFamily(theme.fontFamily)};
    --syv-radius: ${BORDER_RADIUS_MAP[theme.borderRadius || 'md']};
    --syv-shadow: ${SHADOW_MAP[theme.shadow || 'none']};
    --syv-gap: ${spacing.gap};
    --syv-padding: ${spacing.padding};
    --syv-align: ${theme.textAlign || 'left'};
  `.trim()
}

/**
 * Generate the complete CSS for the widget.
 */
export function generateWidgetCSS(instanceId: string, theme: ThemeConfig = {}): string {
  const cssVars = generateCSSVariables(theme)
  const inputStyle = theme.inputStyle || 'outline'
  const buttonStyle = theme.buttonStyle || 'solid'

  return `
/* AgentBase Embed Widget Styles - ${instanceId} */
.${instanceId} {
  ${cssVars}
  font-family: var(--syv-font);
  color: var(--syv-text);
  background: var(--syv-background);
  padding: var(--syv-padding);
  border-radius: var(--syv-radius);
  box-shadow: var(--syv-shadow);
}

.${instanceId} * {
  box-sizing: border-box;
}

/* Form container */
.${instanceId} .syv-form {
  display: flex;
  flex-direction: column;
  gap: var(--syv-gap);
}

/* Field wrapper */
.${instanceId} .syv-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

/* Label */
.${instanceId} .syv-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--syv-text);
}

.${instanceId} .syv-label-required::after {
  content: ' *';
  color: var(--syv-primary);
}

/* Description */
.${instanceId} .syv-description {
  font-size: 0.75rem;
  color: var(--syv-muted);
}

/* Input base styles */
.${instanceId} .syv-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-family: var(--syv-font);
  color: var(--syv-text);
  background: var(--syv-input-bg);
  transition: border-color 0.2s, box-shadow 0.2s;
}

/* Input style variants */
${inputStyle === 'outline' ? `
.${instanceId} .syv-input {
  border: 1px solid var(--syv-border);
  border-radius: var(--syv-radius);
}
.${instanceId} .syv-input:focus {
  outline: none;
  border-color: var(--syv-primary);
  box-shadow: 0 0 0 2px rgba(232, 108, 74, 0.2);
}
` : ''}

${inputStyle === 'filled' ? `
.${instanceId} .syv-input {
  border: none;
  border-radius: var(--syv-radius);
  background: var(--syv-border);
}
.${instanceId} .syv-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(232, 108, 74, 0.2);
}
` : ''}

${inputStyle === 'underline' ? `
.${instanceId} .syv-input {
  border: none;
  border-bottom: 1px solid var(--syv-border);
  border-radius: 0;
  background: transparent;
}
.${instanceId} .syv-input:focus {
  outline: none;
  border-bottom-color: var(--syv-primary);
}
` : ''}

.${instanceId} .syv-input::placeholder {
  color: var(--syv-muted);
}

/* Textarea */
.${instanceId} .syv-textarea {
  min-height: 6rem;
  resize: vertical;
}

/* Select */
.${instanceId} .syv-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b6b6b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  padding-right: 2rem;
}

/* Checkbox */
.${instanceId} .syv-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.${instanceId} .syv-checkbox {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  accent-color: var(--syv-primary);
}

/* Button base styles */
.${instanceId} .syv-button {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: var(--syv-font);
  border-radius: var(--syv-radius);
  cursor: pointer;
  transition: opacity 0.2s, background-color 0.2s;
}

.${instanceId} .syv-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Button style variants */
${buttonStyle === 'solid' ? `
.${instanceId} .syv-button-primary {
  background: var(--syv-primary);
  color: white;
  border: none;
}
.${instanceId} .syv-button-primary:hover:not(:disabled) {
  opacity: 0.9;
}
.${instanceId} .syv-button-secondary {
  background: var(--syv-border);
  color: var(--syv-text);
  border: none;
}
.${instanceId} .syv-button-secondary:hover:not(:disabled) {
  opacity: 0.9;
}
` : ''}

${buttonStyle === 'outline' ? `
.${instanceId} .syv-button-primary {
  background: transparent;
  color: var(--syv-primary);
  border: 1px solid var(--syv-primary);
}
.${instanceId} .syv-button-primary:hover:not(:disabled) {
  background: var(--syv-primary);
  color: white;
}
.${instanceId} .syv-button-secondary {
  background: transparent;
  color: var(--syv-text);
  border: 1px solid var(--syv-border);
}
.${instanceId} .syv-button-secondary:hover:not(:disabled) {
  background: var(--syv-border);
}
` : ''}

${buttonStyle === 'ghost' ? `
.${instanceId} .syv-button-primary {
  background: transparent;
  color: var(--syv-primary);
  border: none;
}
.${instanceId} .syv-button-primary:hover:not(:disabled) {
  background: rgba(232, 108, 74, 0.1);
}
.${instanceId} .syv-button-secondary {
  background: transparent;
  color: var(--syv-text);
  border: none;
}
.${instanceId} .syv-button-secondary:hover:not(:disabled) {
  background: var(--syv-border);
}
` : ''}

/* Button row */
.${instanceId} .syv-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

/* Progress bar */
.${instanceId} .syv-progress {
  height: 4px;
  background: var(--syv-border);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.${instanceId} .syv-progress-bar {
  height: 100%;
  background: var(--syv-primary);
  transition: width 0.3s ease;
}

/* Before the run reports its node count there is no honest percentage, so the
   bar sweeps instead of claiming a position. */
.${instanceId} .syv-progress-indeterminate {
  position: relative;
  overflow: hidden;
}

.${instanceId} .syv-progress-indeterminate::after {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 40%;
  background: var(--syv-primary);
  animation: syv-sweep 1.4s ease-in-out infinite;
}

@keyframes syv-sweep {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}

/* Progress text */
.${instanceId} .syv-progress-text {
  font-size: 0.75rem;
  color: var(--syv-muted);
  margin-bottom: 0.5rem;
}

/* Output section */
.${instanceId} .syv-outputs {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--syv-border);
}

.${instanceId} .syv-output {
  margin-bottom: 1rem;
}

.${instanceId} .syv-output-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--syv-muted);
  margin-bottom: 0.25rem;
}

.${instanceId} .syv-output-value {
  font-size: 0.875rem;
  color: var(--syv-text);
  white-space: pre-wrap;
  word-break: break-word;
}

/* Error message */
.${instanceId} .syv-error {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.${instanceId} .syv-error-title {
  font-weight: 600;
}

/* Loading spinner */
.${instanceId} .syv-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--syv-border);
  border-top-color: var(--syv-primary);
  border-radius: 50%;
  animation: syv-spin 0.6s linear infinite;
}

@keyframes syv-spin {
  to { transform: rotate(360deg); }
}

/* App header: name, description and user manual */
.${instanceId} .syv-header {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  text-align: var(--syv-align);
}

.${instanceId} .syv-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--syv-text);
}

.${instanceId} .syv-app-description {
  margin: 0;
  font-size: 0.875rem;
  color: var(--syv-muted);
}

.${instanceId} .syv-manual {
  padding: 0.75rem;
  border: 1px solid var(--syv-border);
  border-radius: var(--syv-radius);
  font-size: 0.8125rem;
  color: var(--syv-text);
  text-align: var(--syv-align);
}

.${instanceId} .syv-manual > *:first-child { margin-top: 0; }
.${instanceId} .syv-manual > *:last-child { margin-bottom: 0; }
.${instanceId} .syv-manual p { margin: 0 0 0.5rem; }
.${instanceId} .syv-manual h3,
.${instanceId} .syv-manual h4,
.${instanceId} .syv-manual h5,
.${instanceId} .syv-manual h6 {
  margin: 0.75rem 0 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
}
.${instanceId} .syv-manual ul,
.${instanceId} .syv-manual ol {
  margin: 0 0 0.5rem;
  padding-left: 1.25rem;
}
.${instanceId} .syv-manual code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.9em;
  background: var(--syv-border);
  border-radius: 3px;
  padding: 0.05em 0.3em;
}
.${instanceId} .syv-manual a { color: var(--syv-primary); }

/* Branding footer */
.${instanceId} .syv-branding {
  margin-top: 0.75rem;
  font-size: 0.75rem;
  text-align: center;
  color: var(--syv-muted);
}

.${instanceId} .syv-branding a {
  color: inherit;
  text-decoration: none;
}

.${instanceId} .syv-branding a:hover {
  text-decoration: underline;
}

/* Empty state — an app whose surface exposes no fields */
.${instanceId} .syv-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 1.5rem 1rem;
  text-align: center;
  border: 1px dashed var(--syv-border);
  border-radius: var(--syv-radius);
}

.${instanceId} .syv-empty-icon {
  font-size: 1.5rem;
  line-height: 1;
  color: var(--syv-muted);
}

.${instanceId} .syv-empty-title {
  font-size: 0.875rem;
  font-weight: 600;
}

.${instanceId} .syv-empty-text {
  font-size: 0.8125rem;
  color: var(--syv-muted);
  max-width: 32ch;
}

/* Helper text under a control */
.${instanceId} .syv-hint {
  font-size: 0.75rem;
  color: var(--syv-muted);
}

/* File + audio widgets */
.${instanceId} .syv-file,
.${instanceId} .syv-audio {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
}

/* The native picker is driven by the styled button next to it. Kept in the
   layout (not display:none) so assistive tech can still reach the control. */
.${instanceId} .syv-file-native {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.${instanceId} .syv-file-trigger {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.${instanceId} .syv-file-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.${instanceId} .syv-file-chip,
.${instanceId} .syv-audio-player {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border: 1px solid var(--syv-border);
  border-radius: var(--syv-radius);
  background: var(--syv-input-bg);
  font-size: 0.8125rem;
}

.${instanceId} .syv-audio-player {
  flex-wrap: wrap;
}

.${instanceId} .syv-audio-element {
  flex: 1 1 100%;
  width: 100%;
  min-width: 0;
}

/* List input */
.${instanceId} .syv-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
}

.${instanceId} .syv-list-items {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.${instanceId} .syv-list-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.${instanceId} .syv-list-item .syv-input {
  flex: 1;
  min-width: 0;
}

.${instanceId} .syv-file-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.${instanceId} .syv-file-size {
  color: var(--syv-muted);
  font-variant-numeric: tabular-nums;
}

.${instanceId} .syv-icon-button {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  line-height: 1;
  font-size: 1rem;
  border: none;
  border-radius: var(--syv-radius);
  background: transparent;
  color: var(--syv-muted);
  cursor: pointer;
}

.${instanceId} .syv-icon-button:hover {
  background: var(--syv-border);
  color: var(--syv-text);
}

/* Upload / record switch */
.${instanceId} .syv-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem;
  padding: 0.25rem;
  border-radius: var(--syv-radius);
  background: var(--syv-border);
}

.${instanceId} .syv-tab {
  padding: 0.375rem 0.5rem;
  font-size: 0.8125rem;
  font-family: var(--syv-font);
  font-weight: 500;
  border: none;
  border-radius: calc(var(--syv-radius) - 2px);
  background: transparent;
  color: var(--syv-muted);
  cursor: pointer;
}

.${instanceId} .syv-tab-active {
  background: var(--syv-input-bg);
  color: var(--syv-text);
}

.${instanceId} .syv-tab:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.${instanceId} .syv-recording {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.5rem 0.625rem;
  border: 1px solid var(--syv-border);
  border-radius: var(--syv-radius);
  font-size: 0.8125rem;
}

.${instanceId} .syv-recording-dot {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  background: #dc2626;
  animation: syv-pulse 1.2s ease-in-out infinite;
}

.${instanceId} .syv-recording-label { flex: 1; font-weight: 500; }

.${instanceId} .syv-recording-time { font-variant-numeric: tabular-nums; }

@keyframes syv-pulse {
  50% { opacity: 0.35; }
}

/* Reduced motion: the spinner and the recording dot are status indicators, so
   they slow to a crawl rather than stopping outright. */
@media (prefers-reduced-motion: reduce) {
  .${instanceId} .syv-spinner { animation-duration: 2s; }
  .${instanceId} .syv-recording-dot { animation-duration: 3s; }
  .${instanceId} .syv-progress-indeterminate::after { animation: none; width: 100%; opacity: 0.4; }
}

/* Layout variants */
.${instanceId}.syv-layout-horizontal .syv-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--syv-gap);
}

.${instanceId}.syv-layout-compact .syv-input {
  padding: 0.375rem 0.625rem;
  font-size: 0.8125rem;
}

.${instanceId}.syv-layout-compact .syv-button {
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
}
`
}
